import { motion } from 'framer-motion'
import { LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

interface StatCardProps {
    icon: LucideIcon
    label: string
    value: string | number
    subtext?: string
    trend?: 'up' | 'down' | 'neutral'
    trendValue?: string
    color?: 'blue' | 'green' | 'amber' | 'purple' | 'red'
    className?: string
}

const colorClasses = {
    blue: 'from-blue-500 to-blue-600 text-blue-500 bg-blue-500/10',
    green: 'from-green-500 to-green-600 text-green-500 bg-green-500/10',
    amber: 'from-amber-500 to-amber-600 text-amber-500 bg-amber-500/10',
    purple: 'from-purple-500 to-purple-600 text-purple-500 bg-purple-500/10',
    red: 'from-red-500 to-red-600 text-red-500 bg-red-500/10',
}

export function StatCard({
    icon: Icon,
    label,
    value,
    subtext,
    trend,
    trendValue,
    color = 'blue',
    className = ''
}: StatCardProps) {
    const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus

    return (
        <GlassmorphicCard className={`p-4 md:p-5 relative overflow-hidden group ${className}`}>
            {/* Background Glow */}
            <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${colorClasses[color].split(' ')[0]} ${colorClasses[color].split(' ')[1]} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`} />

            <div className="relative z-10 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <p className="text-xs md:text-sm text-theme-secondary font-medium mb-1 truncate">{label}</p>
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-baseline gap-2"
                    >
                        <span className={`text-2xl md:text-3xl font-bold ${colorClasses[color].split(' ')[2]}`}>
                            {value}
                        </span>
                        {subtext && (
                            <span className="text-xs text-theme-muted">{subtext}</span>
                        )}
                    </motion.div>
                    {trend && trendValue && (
                        <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-slate-400'
                            }`}>
                            <TrendIcon className="h-3 w-3" />
                            <span>{trendValue}</span>
                        </div>
                    )}
                </div>
                <div className={`p-2.5 md:p-3 rounded-xl ${colorClasses[color].split(' ')[3]}`}>
                    <Icon className={`h-5 w-5 md:h-6 md:w-6 ${colorClasses[color].split(' ')[2]}`} />
                </div>
            </div>
        </GlassmorphicCard>
    )
}
